import React, { useEffect, useMemo, useState } from 'react';
import Navbar from './Navbar';
import Home from './home/Home';
import Portfolio from './portfolio/Portfolio';
import About from './about/About';
import Experience from './experience/Experience';
import Footer from './footer/Footer';
import Grain from './ui/Grain';
import CommandK from './terminal/CommandK';
import ContactModal from './contact/ContactModal';
import ResumeRequestModal from './resume/ResumeRequestModal';
import CustomCursor from './CustomCursor/CustomCursor';
import useScrollObserver from '../hooks/useScrollObserver';

export default function BaseLayout() {
    const [darkMode, setDarkMode] = useState(
        () => localStorage.getItem('darkMode') !== 'false'
    );
    const [active, setActive] = useState('top');
    const [terminalOpen, setTerminalOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const [resumeOpen, setResumeOpen] = useState(false);

    const refHome = useScrollObserver(setActive);
    const refWork = useScrollObserver(setActive);
    const refAbout = useScrollObserver(setActive);
    const refExperience = useScrollObserver(setActive);
    const refContact = useScrollObserver(setActive);

    useEffect(() => {
        const theme = darkMode ? 'dark' : 'light';
        const bgColor = darkMode ? '#0a0a0b' : '#f2f0eb';
        localStorage.setItem('darkMode', String(darkMode));
        document.documentElement.setAttribute('data-theme', theme);
        document.documentElement.style.background = bgColor;
        const metaTheme = document.querySelector('meta[name="theme-color"]');
        if (metaTheme) metaTheme.setAttribute('content', bgColor);
    }, [darkMode]);

    // /resume.pdf and /resume redirect here with ?resume=1 — open the request
    // form directly so an old link still lands somewhere useful.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('resume')) return;
        setResumeOpen(true);
        params.delete('resume');
        const qs = params.toString();
        window.history.replaceState(
            {}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
        );
    }, []);

    // ⌘K / Ctrl+K opens the portfolio terminal
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setTerminalOpen((v) => !v);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const openTerminal = useMemo(() => () => setTerminalOpen(true), []);
    const openContact = useMemo(() => () => { setTerminalOpen(false); setContactOpen(true); }, []);
    const openResume = useMemo(() => () => { setTerminalOpen(false); setResumeOpen(true); }, []);

    return (
        <>
            <CustomCursor />
            <Grain />
            <Navbar
                darkMode={darkMode}
                handleClick={() => setDarkMode(!darkMode)}
                active={active}
                onOpenTerminal={openTerminal}
            />
            <main>
                <Home innerRef={refHome} onOpenTerminal={openTerminal} />
                <Portfolio innerRef={refWork} />
                <About innerRef={refAbout} onOpenResume={openResume} />
                <Experience innerRef={refExperience} />
            </main>
            <Footer innerRef={refContact} onOpenContact={openContact} onOpenResume={openResume} />
            <CommandK
                open={terminalOpen}
                onClose={() => setTerminalOpen(false)}
                onOpenContact={openContact}
                onOpenResume={openResume}
            />
            <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
            <ResumeRequestModal open={resumeOpen} onClose={() => setResumeOpen(false)} />

            {/* Mobile terminal trigger — desktop uses ⌘K in the nav */}
            <button
                onClick={openTerminal}
                aria-label="open portfolio terminal"
                style={{
                    position: 'fixed',
                    right: '1.1rem',
                    bottom: '1.1rem',
                    zIndex: 60,
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    width: '46px',
                    height: '46px',
                    borderRadius: '50%',
                    background: 'var(--accent)',
                    color: '#0a0a0b',
                    boxShadow: '0 8px 24px -8px var(--accent)',
                    display: terminalOpen ? 'none' : undefined,
                }}
                className="mobileTerminalFab"
            >
                {'>_'}
            </button>
        </>
    );
}
